import type { SimpleSourceTag, UtilizationTag } from "./keyword";
import type { LandscapeRunStatus } from "./landscape-log";

export type Landscape = {
  id: number;
  organization_id: number;
  product_id?: number | null;
  hero_asin: string;
  search_phrase: string;
  status: string;
  status_info: string | null;
  report_file: string | null;
  report_url: string | null;
  created_at: string;
  updated_at: string;
};

export type CreateLandscapeResponse = {
  id: number;
  landscape_id: number;
  landscape: {
    id: number;
    product_id: string | null;
    hero_asin: string;
    search_phrase: string;
    status: string;
    status_info: string | null;
    created_at: string;
    updated_at: string;
  };
};

export type ScrapeCompetitorsResponse = {
  asins: string[];
};

export type LandscapeStatusResponse = {
  keyword_id: number;
  status: string;
  keyword: import('./keyword').Keyword;
};

export type SnowflakeDataResponse = {
  message: string;
  query_id: number;
  data: unknown[];
};

export type SnowflakeDataPendingResponse = {
  message: string;
  query_id: number;
  statement_handle: string;
  status: 'pending';
};

export type CreateLandscapeParams = {
  hero_asin: string;
  search_phrase: string;
  brand_id: number;
};

export type ScrapeCompetitorsParams = {
  keyword: string;
  country: number;
  country_code: string;
  hero_asin?: string;
};

export type FetchSnowflakeDataParams = {
  asins: string[];
  brand_id: number;
};

export type BulkDeleteLandscapesParams = {
  ids: number[];
};

export type BulkRefreshLandscapesParams = {
  landscapes: Array<{
    search_phrase: string;
    hero_asin: string;
    brand_id: number;
  }>;
  /** Opt-in to duplicate refusal (`decisions/the-add-landscapes-dialog-opts-in-to-duplicate-refusal-on-bulk-refresh.md`). */
  refuse_duplicates?: boolean;
  /** The hero product the duplicates are checked against; sent with `refuse_duplicates`. */
  hero_product_id?: number;
};

export type BulkRefreshLandscapesResponse = {
  landscape_ids: number[];
  created: { index: number; landscape_id: number; hero_asin: string; search_phrase: string }[];
  failed: { index: number; hero_asin: string; search_phrase: string; error: string }[];
  /** Absent from a backend that predates the opt-in; read it with `?? []`. */
  duplicates: { index: number; hero_asin: string; search_phrase: string }[];
};

export type LandscapeItem = {
  id: string;
  date: string;
  /**
   * The reported `WorkRunStatus`, or `null` when the landscape has no
   * `work_runs` row (`components/work-run-log.md § What
   * LandscapeController::index takes from here`). No longer a raw
   * `LandscapeStatus` -- read it through `@/utils/landscapeRunStatus`
   * (`isLandscapeRunOpenable`, `runStatusLabel`,
   * `landscapeRunKeywordCountDisplay`), never re-derived locally.
   */
  status: LandscapeRunStatus | null;
  status_info: string | null;
  report_url: string | null;
  top4Hero: number;
  top4CompetitorAvg: number;
  page1Hero: number;
  page1CompetitorAvg: number;
  /**
   * Always a number, `0` when unknown -- unchanged by this task
   * (`components/work-run-log.md`: "no fourth file joins this table").
   * Whether the keyword-count cell shows it or `—` is decided by `status`,
   * via `landscapeRunKeywordCountDisplay`.
   */
  keywordCount: number;
  /**
   * The three instants `FEATURE-005` adds, mirroring `work_runs`. All null
   * together when there is no run row. Rendered nowhere by this task -- no
   * Duration column is added here -- but carried because the response does
   * (`conventions/08-api-contracts.md`).
   */
  started_at: string | null;
  ended_at: string | null;
  state_changed_at: string | null;
};

export type ConsolidatedLandscape = {
  id: string;
  no: number;
  prioritySearchPhrase: string;
  instances?: LandscapeItem[];
};

export type GetLandscapesResponse = {
  landscapes: ConsolidatedLandscape[];
};

export type DashboardRecentLandscape = {
  id: string;
  phrase: string;
  heroAsin: string;
  heroProductId: number | null;
  date: string;
  keywordCount: number;
};

export type GetRecentLandscapesResponse = {
  landscapes: DashboardRecentLandscape[];
};

export type ProductMetrics = {
  id: string;
  position: number;
  isHero: boolean;
  brand: string;
  title: string;
  asin: string;
  imageUrl: string | null;
  reviews: number | null;
  rating: number | null;
  price: number | null;
  bsr: number | null;
  estMonthlyUnits: number | null;
  estMonthlyRevenue: number | null;
  capturedSearchVol: number;
  revVolRatio: number | null;
  top15Positions: number;
};

export type KeywordRanking = {
  id: string;
  keyword: string;
  competitors: number;
  exactVol: number;
  ranks: (number | null)[];
  kpg: number | null;
  search_volume: number;
  ppc_max_bid: number | null;
  hero_relevance: number | null;
  keyword_sales: number | null;
  asin_density: number | null;
  is_master_list?: boolean;
  /** AUT-496: SERP Match, the same six fields the keyword bank reads. Authenticated `show` only, absent from the public share payload. */
  serp_match_pct?: number | null;
  serp_match_organic_pct?: number | null;
  serp_match_sponsored_pct?: number | null;
  serp_match_scored?: number | null;
  serp_match_found?: number | null;
  serp_match_week?: string | null;
  /** ResolveHeroRankSibling::resolve() — describes ranks[0], the hero's rank. */
  hero_rank_asin?: string | null;
  hero_rank_from_sibling?: boolean;
  /** AUT-472: source codes (SimpleSourceTag values), unioned across the product's rows for this search. Authenticated `show` only, absent from the public share payload. */
  sources?: SimpleSourceTag[];
  /** AUT-472: utilization codes (UtilizationTag values), unioned like the bank. Authenticated `show` only, absent from the public share payload. */
  utilization?: UtilizationTag[];
};

export type FactorAvailabilityStatus =
  | "available"
  | "unavailable"
  | "unavailable_no_js_coverage"
  | "pending"
  | "computed_via_competitor_category"
  | "unavailable_no_category";

export type FactorAvailability = {
  search_volume: FactorAvailabilityStatus;
  search_trend: FactorAvailabilityStatus;
  hero_rank: FactorAvailabilityStatus;
  hero_relevance: FactorAvailabilityStatus;
  asin_density: FactorAvailabilityStatus;
  keyword_sales: FactorAvailabilityStatus;
};

export type CategorySource = "OWN" | "COMPETITORS" | "NONE";

export type LandscapeOutputData = {
  id: string;
  prioritySearchPhrase: string;
  date: string;
  totals: {
    relevantKeywords: number;
    exactVolume: number;
    monthlyUnits: number;
  };
  hero_top_positions: number | null | undefined;
  hero_captured_search_volume: number | null | undefined;
  hero_revenue_per_volume_ratio: number | null | undefined;
  products: ProductMetrics[];
  keywords: KeywordRanking[];
  factor_availability?: FactorAvailability | null;
  points_available?: number | null;
  category_source?: CategorySource | null;
};

export enum LandscapeTab {
  Keyword = "keyword",
  Competitor = "competitor",
}
